// frontend/src/components/tables/SimRunsListTable.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * props
 * - onSelect(row): 행 클릭 시 호출
 * - onClose(): (옵션) 닫기 버튼 등에서 호출 가능
 * - selectedId: (옵션) 현재 선택된 run id 표시용
 */
export default function SimRunsListTable({ onSelect, onClose, selectedId }) {
  const API = process.env.REACT_APP_API_URL;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRefresh, setLoadingRefresh] = useState(false);

  const fetchSimRuns = useCallback(async () => {
    setLoadingRefresh(true);
    try {
      const res = await axios.get(`${API}/sim/runs`, {
        params: { limit: 1000, status: 'finished' },
      });

      const list = Array.isArray(res.data) ? res.data : [];

      // 최신 날짜(start_kst) 기준 내림차순 정렬
      list.sort((a, b) => {
        const ta = Date.parse(a?.start_kst ?? '') || 0;
        const tb = Date.parse(b?.start_kst ?? '') || 0;
        return tb - ta; // 최신이 위로
      });

      setRows(list);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
      setLoadingRefresh(false);
    }
  }, [API]);

  useEffect(() => {
    fetchSimRuns();
  }, [fetchSimRuns]);

  if (loading)
    return <p className="text-center text-neutral-500">Loading...</p>;

  return (
    <div className="border border-gray-200 rounded">
      <div className="flex items-center justify-between p-2 border-b bg-gray-50">
        <div className="text-sm font-semibold">목록</div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchSimRuns()}
            disabled={loadingRefresh}
            className="rounded border px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-60"
          >
            {loadingRefresh ? '갱신중…' : '목록 새로고침'}
          </button>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-300 border-b">
          <tr>
            <th className="text-center p-2">번호</th>
            <th className="text-center p-2">날짜</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="text-gray-400 text-center p-3" colSpan={2}>
                시뮬레이션 내역이 없습니다.
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const idStr = row?.id != null ? String(row.id) : '';
              const dateStr = row?.start_kst
                ? String(row.start_kst).slice(0, 10)
                : '';
              const isSelected =
                selectedId != null && String(selectedId) === idStr;

              return (
                <tr
                  key={`${row.id}-${row.start_kst}`}
                  className={`cursor-pointer border-b hover:bg-yellow-50 ${
                    isSelected ? 'bg-yellow-100' : 'bg-slate-50'
                  }`}
                  onClick={() => {
                    if (typeof onSelect === 'function') onSelect(row);
                  }}
                  title="클릭하면 해당 번호/날짜의 시뮬레이션이 적용됩니다."
                >
                  <td className="text-center p-2">{idStr}</td>
                  <td className="text-center p-2">{dateStr}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
